import "./address-form-fields.css";

import type {
	FieldRenderProps,
	FormRenderProps,
} from "@tql/ui-components-react/kendo/form";
import { Field } from "@tql/ui-components-react/kendo/form";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";

import type { SearchResult } from "~/shared/components/fields/address-postal-code-search.tsx";
import { AddressPostalCodeSearch } from "~/shared/components/fields/address-postal-code-search.tsx";
import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";

export const ADDRESS_BOOK_ADDRESS1_MAX_LENGTH = 50;

const companyNameValidator = (value: string): string => {
	if (!value || value.trim() === "") {
		return "Company Name is required";
	}
	return "";
};

const createAddress1Validator =
	(label: string) =>
	(value: string): string => {
		if (!value || value.trim() === "") {
			return `${label} is required`;
		}
		if (value.length > ADDRESS_BOOK_ADDRESS1_MAX_LENGTH) {
			return `${label} cannot exceed ${ADDRESS_BOOK_ADDRESS1_MAX_LENGTH} characters`;
		}
		return "";
	};

export const address1Validator = createAddress1Validator("Address 1");
const addressLine1Validator = createAddress1Validator("Address Line 1");

const postalCodeValidator = (value: ZipCode | null): string => {
	if (!value?.postalCode) {
		return "Postal Code is required";
	}
	return "";
};

const cityValidator = (value: string): string => {
	if (!value || value.trim() === "") {
		return "City is required";
	}
	return "";
};

const stateValidator = (value: string): string => {
	if (!value || value.trim() === "") {
		return "State is required";
	}
	return "";
};

const countryValidator = (value: string): string => {
	if (!value || value.trim() === "") {
		return "Country is required";
	}
	return "";
};

const FormPostalCodeSearch = (fieldRenderProps: FieldRenderProps) => {
	const validationMessage = fieldRenderProps.validationMessage as
		| string
		| undefined;
	const touched = fieldRenderProps.touched as boolean | undefined;
	const label = fieldRenderProps["label"] as string | undefined;
	const id = fieldRenderProps["id"] as string;
	const valid = fieldRenderProps.valid as boolean | undefined;
	const disabled = fieldRenderProps["disabled"] as boolean | undefined;
	const value = fieldRenderProps.value as ZipCode | null;
	const onChange = fieldRenderProps.onChange as (e: {
		value: ZipCode | null;
	}) => void;
	const onSelectionChange = fieldRenderProps["onSelectionChange"] as
		| ((result: SearchResult | null) => void)
		| undefined;

	const showValidationMessage = touched && validationMessage;
	const errorId = showValidationMessage ? `${id}_error` : "";

	return (
		<div className={"postal-code-wrapper"}>
			<Label
				editorDisabled={Boolean(disabled)}
				editorId={String(id)}
				editorValid={Boolean(valid)}
			>
				{label}
				<span className={"required"}>&nbsp;*</span>
			</Label>
			<AddressPostalCodeSearch
				disabled={disabled}
				id={id}
				onSelectionChange={(result) => {
					onChange({
						value: result?.type === "postalCode" ? result.data : null,
					});
					onSelectionChange?.(result);
				}}
				onlySearchPostalCodes={
					fieldRenderProps["onlySearchPostalCodes"] as boolean
				}
				placeholder={fieldRenderProps["placeholder"] as string}
				showFullAddressOnSelect={
					fieldRenderProps["showFullAddressOnSelect"] as boolean
				}
				showIcons={fieldRenderProps["showIcons"] as boolean}
				value={value?.postalCode ?? ""}
			/>
			{showValidationMessage ? (
				<Error id={errorId}>{validationMessage}</Error>
			) : null}
		</div>
	);
};

type AddressFormFieldsProps = Readonly<{
	formRenderProps: FormRenderProps;
	disabled?: boolean;
	/**
	 * Labels the street fields "Address Line 1"/"Address Line 2" instead of
	 * "Address 1"/"Address 2".
	 */
	addressLineLabels?: boolean;
	/**
	 * Shows the required marker on the auto-filled City, State, and Country
	 * fields. They are validated as required either way.
	 */
	showAutoFilledRequiredMarkers?: boolean;
}>;

export function AddressFormFields({
	formRenderProps,
	disabled = false,
	addressLineLabels = false,
	showAutoFilledRequiredMarkers = true,
}: AddressFormFieldsProps) {
	const handlePostalCodeChange = (result: SearchResult | null) => {
		const zipCode = result?.type === "postalCode" ? result.data : null;

		formRenderProps.onChange("addressPostalCode", {
			value: zipCode,
		});
		formRenderProps.onChange("city", {
			value: zipCode?.city ? zipCode.city.name : "",
		});
		formRenderProps.onChange("state", {
			value: zipCode?.city ? zipCode.city.state.abbreviation : "",
		});
		formRenderProps.onChange("country", {
			value: zipCode?.city ? zipCode.city.state.countryCode : "",
		});
	};

	return (
		<div className={"address-form-fields"}>
			<ValidationInputField
				disabled={disabled}
				fieldType={FieldType.INPUT}
				id={"companyName"}
				label={"Company Name"}
				name={"companyName"}
				required={true}
				validator={companyNameValidator}
				wrapperStyle={{ marginTop: 0 }}
			/>
			<ValidationInputField
				disabled={disabled}
				fieldType={FieldType.INPUT}
				id={"address1"}
				label={addressLineLabels ? "Address Line 1" : "Address 1"}
				maxLength={ADDRESS_BOOK_ADDRESS1_MAX_LENGTH}
				name={"address1"}
				required={true}
				validator={
					addressLineLabels ? addressLine1Validator : address1Validator
				}
			/>
			<ValidationInputField
				disabled={disabled}
				fieldType={FieldType.INPUT}
				id={"address2"}
				label={addressLineLabels ? "Address Line 2" : "Address 2"}
				name={"address2"}
				required={false}
			/>

			<div className={"address-form-inline-fields"}>
				<Field
					component={FormPostalCodeSearch}
					disabled={disabled}
					id={"addressPostalCode"}
					label={"Postal Code"}
					name={"addressPostalCode"}
					onSelectionChange={handlePostalCodeChange}
					onlySearchPostalCodes={true}
					placeholder={""}
					showFullAddressOnSelect={false}
					showIcons={false}
					validator={postalCodeValidator}
				/>
				<ValidationInputField
					disabled={true}
					fieldType={FieldType.INPUT}
					id={"city"}
					label={"City"}
					name={"city"}
					required={showAutoFilledRequiredMarkers}
					validator={cityValidator}
				/>
				<ValidationInputField
					disabled={true}
					fieldType={FieldType.INPUT}
					id={"state"}
					label={"State"}
					name={"state"}
					required={showAutoFilledRequiredMarkers}
					validator={stateValidator}
					wrapperStyle={{ width: "100px" }}
				/>
				<ValidationInputField
					disabled={true}
					fieldType={FieldType.INPUT}
					id={"country"}
					label={"Country"}
					name={"country"}
					required={showAutoFilledRequiredMarkers}
					validator={countryValidator}
				/>
			</div>
		</div>
	);
}
